// Настройки, выгрузка и удаление аккаунта. Экран 11.
//
// Настройки живут на сервере, а не в браузере: обещание «маскировать данные»
// дано человеку, а не устройству, и на телефоне (P9) должно действовать тоже.

import { ApiError, failureBody, OfflineError, type Client } from './client.ts'

export interface Settings {
  maskPii: boolean
  projectMemory: boolean
  showModel: boolean
}

/** Что исчезнет при удалении и что останется. Показывается ДО подтверждения. */
export interface DeletionTerms {
  /** Слово, которое человек печатает руками. Сервер иначе не примет. */
  confirmationWord: string
  removed: string[]
  kept: { what: string; why: string }[]
  irreversible: boolean
  timing: string
}

/**
 * Текст на ответ фотографии без нашего текста. SMY-332
 *
 * С 21.09.2026 фотография читается со сроком, как загрузка файла (SMY-328), и
 * так же рвётся: api закрывает соединение, когда тело не дошло за срок, и Caddy
 * отвечает на обрыв 502 с пустым телом. «Ошибка 502» под фотографией человеку
 * ничего не говорит. Пара — `UPLOAD_BROKEN` в files.ts.
 */
export const AVATAR_BROKEN = 'Фотография не дошла до сервера: связь оборвалась или она шла слишком долго. Попробуй ещё раз'

export const createAccount = (client: Client) => ({
  settings: () => client.request<Settings>('/v1/settings'),

  /**
   * Имя. Пустая строка — это «убрать», а не «оставить как было»: человек,
   * стерший имя, просит его забыть.
   */
  setName: (name: string) =>
    client.request<{ name: string | null }>('/v1/account/profile', { method: 'PUT', body: { name } }),

  /**
   * Фотография. Идёт мимо `client.request` по той же причине, что и загрузка
   * файлов: тот шлёт JSON, а тут multipart, и границу формы браузер обязан
   * проставить сам — задать `content-type` руками значит отправить форму без
   * границы, и сервер её не разберёт.
   */
  async setAvatar(file: Blob, filename = 'avatar'): Promise<{ avatarUrl: string | null }> {
    const form = new FormData()
    form.append('file', file, filename)

    const token = client.tokens.get()
    let res: Response
    try {
      res = await fetch(`${client.baseUrl}/v1/account/avatar`, {
        method: 'PUT',
        ...(token != null ? { headers: { authorization: `Bearer ${token}` } } : {}),
        body: form,
      })
    } catch (cause) {
      throw new OfflineError(cause)
    }

    const text = await res.text()
    // Отказ — общим разбором клиента (SMY-353): не-JSON страница прокси и
    // английский отказ Fastify тоже идут человеку текстом AVATAR_BROKEN.
    if (!res.ok) throw new ApiError(res.status, failureBody(text, AVATAR_BROKEN))
    let body: Record<string, unknown> = {}
    if (text !== '') {
      try {
        body = JSON.parse(text) as Record<string, unknown>
      } catch {
        body = { error: text.slice(0, 200) }
      }
    }
    return body as { avatarUrl: string | null }
  },

  removeAvatar: () => client.request<{ ok: true }>('/v1/account/avatar', { method: 'DELETE' }),

  /**
   * Завести или сменить пароль. `current` обязателен, если пароль уже задан.
   *
   * Ответ говорит, сколько ЧУЖИХ сеансов погашено: смена пароля выкидывает
   * все остальные устройства, и человеку стоит увидеть, что это случилось, —
   * иначе он узнает об этом, когда телефон попросит войти заново.
   */
  setPassword: (password: string, current?: string) =>
    client.request<{ ok: true; revokedSessions: number }>('/v1/account/password', {
      method: 'PUT',
      body: { password, ...(current != null ? { current } : {}) },
    }),

  /** Снять пароль. Вход по SMS остаётся, поэтому аккаунт не запирается. */
  removePassword: (current: string) =>
    client.request<{ ok: true }>('/v1/account/password', { method: 'DELETE', body: { current } }),

  /** Изменить одну настройку. Ответ — полный набор, каким он стал. */
  setSetting: (patch: Partial<Settings>) =>
    client.request<Settings>('/v1/settings', { method: 'PUT', body: patch }),

  deletionTerms: () => client.request<DeletionTerms>('/v1/account/deletion-terms'),

  /**
   * Удалить аккаунт. Слово подтверждения обязано прийти от человека — не от
   * клиента: случайного нажатия и повтора чужого запроса для необратимого
   * действия мало.
   */
  remove: (confirm: string) =>
    client.request<{ deleted: true; files: number }>('/v1/account/delete', {
      method: 'POST',
      body: { confirm },
    }),

  /**
   * Смена почты: код на НОВЫЙ адрес. SMY-209
   *
   * Здесь, а не в `auth`, потому что это действие профиля: сессия обязательна,
   * и человек не входит, а правит своё. В `auth` живут ручки, которыми входят,
   * и половина из них работает без сессии.
   *
   * Отвечает одинаково и для свободного адреса, и для занятого: «адрес занят»
   * приходит только из `confirmEmailChange`, после кода.
   */
  requestEmailChange: (email: string) =>
    client.request<{ resendAfterSeconds: number }>('/v1/account/email/request-code', {
      method: 'POST',
      body: { email },
    }),

  /**
   * Подтвердить смену кодом с нового адреса.
   *
   * ⚠️ Успех ГАСИТ ОСТАЛЬНЫЕ СЕАНСЫ — текущий остаётся. Клиенту после этого
   * стоит обновить список устройств, если он его показывает: там станет на
   * строку меньше, и объяснить это человеку лучше сразу.
   */
  confirmEmailChange: (email: string, code: string) =>
    client.request<{ email: string }>('/v1/account/email/confirm', {
      method: 'POST',
      body: { email, code },
    }),
})

export type AccountApi = ReturnType<typeof createAccount>
